import { useCallback, useEffect, useState } from "react";
import { KeyRound, Trash2 } from "lucide-react";
import {
  enrolPasskey, listPasskeys, passkeyErrorMessage, passkeysEnabled, passkeysSupported,
  revokePasskey, type StoredPasskey,
} from "../lib/passkey";

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "never";

/** A sensible starting label so the list stays meaningful without the user
 *  having to think of a name — they can still overwrite it. */
function suggestLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android phone";
  if (/Mac OS X/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  return "This device";
}

export default function PasskeySettings() {
  const [available, setAvailable] = useState<boolean | null>(null);
  const [keys, setKeys] = useState<StoredPasskey[]>([]);
  const [label, setLabel] = useState(suggestLabel);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(() => { void listPasskeys().then(setKeys).catch(() => {}); }, []);

  useEffect(() => {
    if (!passkeysSupported()) { setAvailable(false); return; }
    void passkeysEnabled().then((on) => { setAvailable(on); if (on) reload(); });
  }, [reload]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setMsg(null); setBusy(true);
    try {
      await enrolPasskey(label.trim() || suggestLabel());
      setMsg("Passkey added — you can now sign in without your password.");
      reload();
    } catch (e2) {
      setErr(passkeyErrorMessage(e2));
    } finally {
      setBusy(false);
    }
  }

  async function remove(key: StoredPasskey) {
    if (!confirm(`Remove "${key.label}"? You will no longer be able to sign in with it.`)) return;
    setErr(null); setMsg(null);
    try {
      await revokePasskey(key.id);
      setMsg(`Removed "${key.label}".`);
      reload();
    } catch (e2) {
      setErr(passkeyErrorMessage(e2));
    }
  }

  if (available === null) return null;

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-medium text-ink">Passkeys</h2>
      <div className="card p-5 space-y-4 max-w-xl">
        <p className="text-sm text-mut">
          Sign in with your fingerprint, face, or device PIN instead of a password. A passkey
          cannot be phished or reused on a copycat site — it only works on this one.
        </p>

        {!available ? (
          <p className="text-sm text-faint">Passkeys are not available in this browser.</p>
        ) : (
          <>
            {keys.length > 0 && (
              <ul className="divide-y divide-edge-soft border-y border-edge-soft">
                {keys.map((k) => (
                  <li key={k.id} className="flex items-center gap-3 py-3">
                    <KeyRound className="h-4 w-4 text-teal-700 shrink-0" aria-hidden="true" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ink truncate">{k.label}</p>
                      <p className="text-xs text-faint">
                        Added {when(k.createdAt)} · last used {when(k.lastUsedAt)}
                        {k.backedUp && " · synced across your devices"}
                      </p>
                    </div>
                    <button onClick={() => remove(k)} aria-label={`Remove passkey ${k.label}`}
                      className="btn-secondary px-2 py-1.5">
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <form onSubmit={add} className="flex items-end gap-2">
              <label className="block flex-1">
                <span className="label">Device name</span>
                <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60}
                  className="input" placeholder="Work laptop" />
              </label>
              <button disabled={busy} className="btn-dark whitespace-nowrap">
                {busy ? "Waiting…" : "Add a passkey"}
              </button>
            </form>
          </>
        )}

        {msg && <p role="status" className="text-sm text-teal-700">{msg}</p>}
        {err && <p role="alert" className="text-sm text-red-600">{err}</p>}
      </div>
    </section>
  );
}
